<script setup lang="ts">
import { CreatorStatusFilterEnum, type CreatorsV2Filter, ProfileOptionItem } from '@metapic-apps/shared'
import { computed, ref, watch } from 'vue'

import { useGetUserListRequest } from '@/api/users.api'
import { debounce } from '@/lib/helpers'
import { SelectComponent } from '@/ui-library'

const props = defineProps<{ advertiserGroupId: number }>()
const emit = defineEmits<(e: 'creator-change', creatorId?: number) => void>()
const remoteQuery = ref<string>('')
const debouncedRemoteQuery = ref<string>('')
watch(debouncedRemoteQuery, () => {
  isLoading.value = true
})
watch(
  remoteQuery,
  debounce(
    (value: string) => {
      debouncedRemoteQuery.value = value
    },
    300,
    {
      leading: true,
    },
  ),
)

const handleRemoteQuery = (query: string) => {
  remoteQuery.value = query
}
const selectedCreatorId = ref<number>()
const handleItemSelect = (item: { id: number; label: string }) => {
  selectedCreatorId.value = item.id
  emit('creator-change', item.id)
}
const handleClear = () => {
  selectedCreatorId.value = undefined
  emit('creator-change')
}

const filter = ref<CreatorsV2Filter>({
  advertiserGroup: {
    id: props.advertiserGroupId,
    name: '',
  },
  status: CreatorStatusFilterEnum.Accepted,
  query: debouncedRemoteQuery.value,
})

watch(
  () => [props.advertiserGroupId, debouncedRemoteQuery.value] as const,
  ([advertiserGroupId, query]) => {
    filter.value = {
      advertiserGroup: {
        id: advertiserGroupId,
        name: '',
      },
      query,
      status: CreatorStatusFilterEnum.Accepted,
    }
  },
  { immediate: true },
)

const { data, isLoading } = useGetUserListRequest(filter)
const options = computed(
  () =>
    data.value?.map((user) => ({
      id: user.id,
      label: `[ID: ${user.id}] ${user.username} - ${user.email}`,
    })) || [],
)

const usersById = computed(() => new Map((data.value ?? []).map((user) => [user.id, user])))
const getUser = (id: number) => usersById.value.get(id)
</script>
<template>
  <SelectComponent
    class="advertiser-details-campaigns-creator-filter__select"
    popper-class="advertiser-details-campaigns-creator-filter__dropdown"
    placeholder="Search creator by ID, email or username"
    remote
    clearable
    filterable
    :is-loading="isLoading"
    :options="options"
    value-key="id"
    :remote-method="handleRemoteQuery"
    :on-item-select="handleItemSelect"
    @clear="handleClear"
  >
    <template #option="{ item }">
      <ProfileOptionItem
        v-if="getUser(item.id)"
        :id="item.id"
        :username="getUser(item.id)!.username"
        :email="getUser(item.id)!.email"
        :image="getUser(item.id)!.image"
        :avatar-name="getUser(item.id)!.displayName"
        :added="selectedCreatorId === item.id"
        indicator="check"
      />
    </template>
  </SelectComponent>
</template>
<style lang="scss" scoped>
.advertiser-details-campaigns-creator-filter__select {
  width: 420px;
}
</style>
<style lang="scss">
.advertiser-details-campaigns-creator-filter__dropdown .el-select-dropdown__item {
  height: auto;
  padding: 0;
  line-height: normal;
  white-space: normal;
}
</style>
